import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  SelectChangeEvent, 
  useTheme,
  FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LocalOffer as OfferIcon } from '@mui/icons-material';
import { getActivePromotions, ActivePromotion } from '../../services/promotionService';
import { useSnackbar } from 'notistack';

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    borderRadius: theme.shape.borderRadius,
    borderColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
    '&:hover': {
      borderColor: theme.palette.text.primary,
    },
    '&.Mui-focused': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.text.primary,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: 1,
  },
}));

interface PromotionFilterProps {
  selectedPromotion: string;
  onSelectPromotion: (promotionId: string, promotionTitle?: string) => void;
  disabled?: boolean;
}

const PromotionFilter: React.FC<PromotionFilterProps> = ({
  selectedPromotion,
  onSelectPromotion,
  disabled = false,
}) => {
  const [promotions, setPromotions] = useState<ActivePromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchActivePromotions = async () => {
      try {
        setLoading(true);
        const activePromotions = await getActivePromotions();
        setPromotions(activePromotions);
      } catch (error) {
        console.error('Error fetching active promotions:', error);
        enqueueSnackbar('Error al cargar las promociones activas', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchActivePromotions();
  }, [enqueueSnackbar]);
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const selectedId = event.target.value as string;
    const selectedPromo = promotions.find(p => p.id.toString() === selectedId);
    onSelectPromotion(selectedId, selectedPromo?.title);
  };

  return (
    <Box sx={{ mb: 4, maxWidth: 400 }}>
      <FormControl fullWidth variant="outlined">
        <InputLabel id="promotion-select-label" sx={{ 
          backgroundColor: theme.palette.background.paper,
          px: 1,
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          },
        }}>
          Seleccionar Promoción
        </InputLabel>
        <StyledSelect
          labelId="promotion-select-label"
          id="promotion-select"
          value={selectedPromotion || ''}
          onChange={handleChange}
          displayEmpty
          disabled={disabled || loading}
          inputProps={{ 'aria-label': 'Seleccionar promoción' }}
          sx={{
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              '& .MuiListItemIcon-root': {
                minWidth: 40,
              },
            },
          }}
        >
          <MenuItem value="">
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <OfferIcon sx={{ color: 'text.secondary', mr: 1 }} />
              <Typography variant="body1">Todas las promociones</Typography>
            </Box>
          </MenuItem>
          {promotions.map((promotion) => (
            <MenuItem key={promotion.id} value={promotion.id.toString()}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box 
                  component="img"
                  src={promotion.img}
                  alt={promotion.title}
                  sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '4px',
                    objectFit: 'cover',
                    mr: 2,
                  }}
                />
                <Typography variant="body1">{promotion.title}</Typography>
              </Box>
            </MenuItem>
          ))}
        </StyledSelect>
        <FormHelperText>
          {loading ? 'Cargando promociones...' : 'Selecciona una promoción para filtrar los resultados'}
        </FormHelperText>
      </FormControl>
    </Box>
  );
};

export default PromotionFilter;
